import { getErc20TokenBySymbol } from '@ui/constants/tokens';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceUpdatesByPosition } from '@ui/services/backend/hooks/oracle/usePriceUpdatesByPosition';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { usePythUpdateFee } from '@ui/services/pyth-hermes/pyth-price-update/usePythUpdateFee';
import { Address } from '@ui/types/alias';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

/**
 * The contract rejects prices older than maxDelayForTrading (30s), so keep
 * refreshing the signed update data while the user sits in the modal —
 * otherwise a stale payload would make the remove tx revert.
 */
const ORACLE_DATA_REFETCH_INTERVAL = 15_000;

export type RemoveMarginOracleData = {
  oracleUpdateData: {
    pythPriceUpdateData: Address[];
    pythProPriceUpdateData: Address[];
  };
  /** native value to send with removeMargin: oracle update fee (+ verification fee) */
  value: bigint;
};

/**
 * removeMargin on-chain reads the pair price and the margin token price with a
 * freshness bound (getPriceForTrading), so the tx must push the oracle update
 * data for both feeds and pay the update fee via msg.value.
 */
export const useRemoveMarginOracleData = (
  position: Position,
): RemoveMarginOracleData | undefined => {
  const chainId = useDataChainId();
  const wrappedNativeToken = getErc20TokenBySymbol('WRAPPED_NATIVE', chainId);
  const collateralForOracle =
    position.collateralToken.isNative && !isNil(wrappedNativeToken)
      ? wrappedNativeToken.address
      : position.collateralToken.address;

  const { data: oracleBatchPrice } = usePriceUpdatesByPosition({
    pairBase: position.pair.address,
    collateral: collateralForOracle,
    enabled: !!position.pair.address && !!collateralForOracle,
    refetchInterval: ORACLE_DATA_REFETCH_INTERVAL,
  });

  const pythPriceUpdateData = useMemo(
    () => (oracleBatchPrice?.pythPriceUpdateData ?? []) as Address[],
    [oracleBatchPrice?.pythPriceUpdateData],
  );
  const pythProPriceUpdateData = useMemo(
    () => (oracleBatchPrice?.pythProPriceUpdateData ?? []) as Address[],
    [oracleBatchPrice?.pythProPriceUpdateData],
  );

  const oraclePythUpdateFee = oracleBatchPrice?.updateFee
    ? BigInt(oracleBatchPrice.updateFee)
    : undefined;
  const verificationFee = oracleBatchPrice?.verifition_fee
    ? BigInt(oracleBatchPrice.verifition_fee)
    : 0n;

  const { data: fallbackPythUpdateFee } = usePythUpdateFee({
    pythUpdateData: pythPriceUpdateData,
  });
  const pythUpdateFee = oraclePythUpdateFee ?? fallbackPythUpdateFee;

  return useMemo(() => {
    if (isNil(oracleBatchPrice) || isNil(pythUpdateFee)) return undefined;

    return {
      oracleUpdateData: {
        pythPriceUpdateData,
        pythProPriceUpdateData,
      },
      value: pythUpdateFee + verificationFee,
    };
  }, [
    oracleBatchPrice,
    pythUpdateFee,
    pythPriceUpdateData,
    pythProPriceUpdateData,
    verificationFee,
  ]);
};
